import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';
import { renderGoal } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-cell/goals-matrix-cell';
import { formatDate } from 'helpers/dates-formatter';

import { getClientGoalRevisions } from 'api/generated/client-goal-revisions/client-goal-revisions';

import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';
import type { ClientGoalRevisionDto } from 'api/generated/schemas';

const ActionTypes: Record<string, string> = {
  INSERT: 'Goal Created',
  UPDATE: 'Goal Updated',
};

interface HistoryProps {
  data: { id: string | number; category: string };
  userId: string | undefined;
  clientId: string | number | undefined;
}

const History = ({
  data: { id, category },
  userId,
  clientId,
}: HistoryProps): React.JSX.Element => {
  const [history, setHistory] = useState<ClientGoalRevisionDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [visibleItems, setVisibleItems] = useState<number>(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    const fetchGoalHistory = async (): Promise<void> => {
      if (!userId || !clientId || !id) {
        setLoading(false);

        return;
      }

      try {
        const historyData = await getClientGoalRevisions(
          userId,
          Number(clientId),
          Number(id)
        );

        setHistory(historyData);

        historyData.forEach((_, index) => {
          const timer = setTimeout(() => {
            setVisibleItems((prev) => prev + 1);
          }, index * 300);

          timers.push(timer);
        });
      } catch (error) {
        console.error('Error while fetching goal history', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoalHistory();

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [userId, clientId, id]);

  const renderRevisionMetadata = (
    createdBy: string | undefined,
    createdDate: string | null
  ): React.JSX.Element => (
    <Box
      sx={{
        color: 'tokens.navy.500',
        fontSize: '0.75rem',
        lineHeight: '1rem',
      }}
    >
      <p data-testid="history-date">{createdDate}</p>
      <p data-testid="history-email">{createdBy}</p>
    </Box>
  );

  const renderContent = (revision: Goal): React.JSX.Element => {
    const renderOtherGoalContent = (
      label: string,
      value: React.ReactNode
    ): React.JSX.Element => (
      <Stack direction="row" sx={{ gap: 0.5, alignItems: 'baseline' }}>
        <span className="text-gray-400 shrink-0">{label}</span>
        <span className="text-gray-600 font-normal"> {value}</span>
      </Stack>
    );

    if (category === 'Other') {
      return (
        <div className="font-semibold font-sm">
          {renderOtherGoalContent(
            'Title:',
            (revision as { name?: string }).name
          )}
          {renderOtherGoalContent('Description:', revision.description)}
        </div>
      );
    }

    return renderGoal(revision);
  };

  const renderAction = (type: string): React.JSX.Element => (
    <p className="font-bold text-navy-800">{ActionTypes[type]}</p>
  );

  const renderGoalVersion = (
    type: string,
    item: Goal | undefined,
    summary: string | undefined
  ): React.JSX.Element => (
    <Stack
      direction={category === 'Other' ? 'column' : 'row'}
      sx={{ gap: 0.5, alignItems: 'baseline', color: 'tokens.gray.600' }}
    >
      <p className="text-gray-700 shrink-0 font-sm">{`${type} Version:`}</p>
      {renderContent(item as Goal)}
      {summary && (
        <span className="font-normal text-gray-600 text-sm">{`, ${summary}`}</span>
      )}
    </Stack>
  );

  const renderHistoryItem = (
    item: ClientGoalRevisionDto,
    index: number
  ): React.JSX.Element => {
    const { createdBy, createdDate, type } = item.metadata ?? {};
    const summary = item.revision?.summary;
    const formattedDate = formatDate(createdDate ?? null, 'MMM D, YY h:mm A');

    return (
      <>
        {renderAction(type ?? '')}
        {renderRevisionMetadata(createdBy, formattedDate)}
        <Stack sx={{ mt: 1, gap: 0.5, fontWeight: 600 }}>
          {type === 'UPDATE' &&
            renderGoalVersion(
              'Prior',
              history[index + 1]?.revision as Goal | undefined,
              history[index + 1]?.revision?.summary
            )}
          {renderGoalVersion(
            type === 'INSERT' ? 'Initial' : 'New',
            item.revision as Goal | undefined,
            summary
          )}
        </Stack>
      </>
    );
  };

  if (loading) {
    return (
      <Loader
        loading={true}
        overlay="white"
        sx={{ opacity: 0.75 }}
        fullScreen={false}
      />
    );
  }

  return (
    <Stack
      sx={{ position: 'relative', alignItems: 'flex-start', height: '100%' }}
      data-testid="goal-history-tab"
    >
      {history.map((item, index) => (
        <div
          key={item.metadata?.createdDate ?? index}
          className={classNames(
            'relative flex items-baseline space-x-4 transition-opacity duration-500 ease-out transform-gpu',
            {
              'opacity-100 translate-y-0': visibleItems > index,
              'opacity-0 translate-y-4': visibleItems <= index,
            }
          )}
          style={{ transitionDelay: `${index * 300}ms` }}
        >
          {history.length > 1 && (
            <Stack sx={{ alignItems: 'center', height: '100%' }}>
              <Box
                sx={{
                  width: '0.75rem',
                  height: '0.75rem',
                  bgcolor: 'tokens.navy.800',
                  borderWidth: '3px',
                  borderColor: 'tokens.navy.200',
                  borderRadius: '9999px',
                  flexShrink: 0,
                }}
              />
              {index < history.length - 1 && (
                <Box
                  sx={[
                    {
                      width: '0.125rem',
                      bgcolor: 'tokens.navy.200',
                      transitionProperty: 'all',
                      transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
                      transitionDuration: '500ms',
                    },
                    visibleItems > index ? { height: '100%' } : { height: 0 },
                  ]}
                  style={{
                    transitionDelay: `${index * 300 + 200}ms`,
                  }}
                />
              )}
            </Stack>
          )}
          <Box sx={{ width: '100%', mb: 1.25 }}>
            {renderHistoryItem(item, index)}
          </Box>
        </div>
      ))}
    </Stack>
  );
};

export default History;
